import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";

import useObjectUrl from "../utils/useObjectUrl";
import { PulseLoader } from "react-spinners";
import { validateAttributes, errorMessage } from "../utils/validations";
import { validations } from "./validations";

const EditCourse = (props) => {
  const { categories, clients, course } = props;

  const [errors, setErrors] = useState("");

  const [title, setTitle] = useState(course.title || "");
  const [description, setDescription] = useState(course.description || "");

  const [selectedCategory, setSelectedCategory] = useState(
    course.categories.map((category) => category.id) || [],
  );
  const [openCategory, setOpenCategory] = useState(false);
  const [categoriesToPick, setCategoriesToPick] = useState(categories);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");

  // Refs for dropdown management
  const categoryDropdownRef = useRef(null);
  const clientDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setOpenCategory(false);
      }
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target)
      ) {
        setOpenClient(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [openClient, setOpenClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(
    course.clients.map((client) => client.id),
  );
  const [clientsToPick, setClientsToPick] = useState(clients);
  const [clientSearchTerm, setClientSearchTerm] = useState("");

  const [file, setFile] = useState(null);
  // Show the newly picked logo straight away; before this, nothing changed until save.
  const logoPreview = useObjectUrl(file);

  const [loading, setLoading] = useState(false);

  const showSelectedCategories = selectedCategory.map((categoryId) => {
    return (
      <span
        key={categoryId}
        className="shrink-0 whitespace-nowrap border-2 border-gray-100 px-2 py-0.5 text-gray-500 text-sm"
      >
        <button
          className="hover:line-through"
          onClick={() => handleCategoryChange(categoryId)}
        >
          {categories.find((cat) => cat.id === categoryId).name}
        </button>
      </span>
    );
  });

  const handleCategoryChange = (categoryId) => {
    if (selectedCategory.includes(categoryId)) {
      setSelectedCategory(selectedCategory.filter((id) => id !== categoryId));
      return;
    }
    setSelectedCategory([...selectedCategory, categoryId]);
    setOpenCategory(false);
  };

  const handleCategoryDropdownToggle = () => {
    setOpenCategory(!openCategory);
    setOpenClient(false); // Close client dropdown
    if (!openCategory) {
      setCategorySearchTerm(""); // Clear search when opening dropdown
    }
  };

  useEffect(() => {
    setCategoriesToPick(
      categories.filter((category) => !selectedCategory.includes(category.id)),
    );

    if (selectedCategory.length === categories.length) {
      setOpenCategory(false);
    }
  }, [course, selectedCategory]);

  // Filter categories based on search term
  const filteredCategories = categoriesToPick.filter((category) =>
    category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()),
  );

  const showSelectedClients = selectedClient.map((clientId) => {
    return (
      <span
        key={clientId}
        className="shrink-0 whitespace-nowrap border-2 border-gray-100 px-2 py-0.5 text-gray-500 text-sm"
      >
        <button
          className="hover:line-through"
          onClick={() => handleClientChange(clientId)}
        >
          {clients.find((client) => client.id === clientId).name}
        </button>
      </span>
    );
  });

  const handleClientChange = (clientId) => {
    if (selectedClient.includes(clientId)) {
      setSelectedClient(selectedClient.filter((id) => id !== clientId));
      return;
    }
    setSelectedClient([...selectedClient, clientId]);
    setOpenClient(false);
  };

  const handleClientDropdownToggle = () => {
    setOpenClient(!openClient);
    setOpenCategory(false); // Close category dropdown
    if (!openClient) {
      setClientSearchTerm(""); // Clear search when opening dropdown
    }
  };

  useEffect(() => {
    setClientsToPick(
      clients.filter((client) => !selectedClient.includes(client.id)),
    );

    if (selectedClient.length === clients.length) {
      setOpenClient(false);
    }
  }, [course, selectedClient]);

  // Filter clients based on search term
  const filteredClients = clientsToPick.filter((client) =>
    client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()),
  );

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const uploadFile = async () => {
    if (!file) return;

    const url = `/courses/presigned_url?filename=${file.name}&content_type=${file.type}`;

    const data = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    }).then((response) => response.json());

    await fetch(data.url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    })
      .then(() => {
        console.log("LOADER STOPS");
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        setFile(null);
      });

    return data.key;
  };

  const handleSumbit = async () => {
    const validation = validateAttributes(
      {
        title,
      },
      validations,
    );

    setErrors(validation);
    if (validation) return;

    setLoading(true);
    const logoData = file
      ? {
          key: await uploadFile(),
          content_type: file.type,
          byte_size: file.size,
        }
      : null;

    const data = await fetch(`/courses/${course.id}`, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        course: {
          logo: logoData,
          title: title,
          description: description,
          category_ids: selectedCategory,
          client_ids: selectedClient,
        },
      }),
    });

    if (data.ok) {
      const response = await data.json();
      window.location.href = response.redirect_to;
    }
  };

  const className = (attribute) => {
    let className = "w-full mt-1 border px-3 py-2 text-sm";
    if (errors && errors[attribute]) {
      className += " border border-red-500 focus:ring-red-500";
    }
    return className;
  };

  return (
    <div className="flex flex-col md:flex-row p-6 gap-6 w-full max-w-full overflow-x-hidden">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <PulseLoader
            loading={loading}
            size={20}
            speedMultiplier={0.5}
            color="#ffffff"
          />
        </div>
      )}
      <div className="flex items-center justify-center w-full h-1/4 flex-col border border-gray-200 p-4">
        {logoPreview || course.logo_url ? (
          <img
            className="mb-4 aspect-video border-gray-100 border-2 text-gray-500"
            height={500}
            src={logoPreview || course.logo_url}
          />
        ) : (
          <div className="mb-4 aspect-video h-64 flex items-center justify-center border-gray-100 border-2 text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
              />
            </svg>
          </div>
        )}
        <div className="flex flex-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18px"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="grey"
            className="size-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
          <span className="text-xs text-gray-500 mb-2 mr-4">
            Rekomendowana propocja to 16:9.
          </span>
        </div>
        <label
          htmlFor="dropzone-file"
          className="text-center border-2 border-primaryYellow text-primaryYellow w-full cursor-pointer"
        >
          <div className="flex items-center justify-center pt-6 pb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
            Wybierz z dysku
          </div>
          <input
            id="dropzone-file"
            type="file"
            className="hidden"
            title=" "
            onChange={(e) => handleFileChange(e)}
            accept="image/*"
          />
        </label>
      </div>

      <div className="w-full md:w-2/3 min-w-0 flex flex-col gap-4">
        <div>
          <label className="text-sm font-medium">Tytuł</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nazwa"
            className={className("title")}
          />
          {errorMessage(errors, "title")}
        </div>

        <div>
          <label className="text-sm font-medium">
            Krótki opis <span className="text-gray-400">(opcjonalnie)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
            placeholder="Opis..."
            className="w-full mt-1 border px-3 py-2 text-sm h-24 resize-none"
          />
          <div className="text-xs text-gray-400 text-right">
            {description.length}/200
          </div>
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-sm font-medium">Kategorie</span>
          <div className="relative inline-block text-left">
            {categoriesToPick.length ? (
              <button
                onClick={handleCategoryDropdownToggle}
                className="flex justify-between w-full border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium hover:bg-gray-50"
              >
                <span className="text-sm font-medium">
                  {categoriesToPick[0].name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-light">dodaj</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </div>
              </button>
            ) : (
              <></>
            )}

            {openCategory && (
              <div
                className="absolute w-full mt-1 border bg-white shadow-lg z-50"
                ref={categoryDropdownRef}
              >
                {/* Search input for categories */}
                <div className="p-3 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Szukaj kategorii..."
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>

                {/* Categories list */}
                <div className="max-h-60 overflow-auto">
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((category) => (
                      <button
                        key={category.id}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                        onClick={() => handleCategoryChange(category.id)}
                      >
                        {category.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Nie znaleziono kategorii
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-1 overflow-x-auto pb-1 min-w-0 max-w-full">{showSelectedCategories}</div>
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-sm font-medium">Klienci</span>
          <div className="relative inline-block text-left">
            {clientsToPick.length ? (
              <button
                onClick={handleClientDropdownToggle}
                className="flex justify-between w-full border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium hover:bg-gray-50"
              >
                <span className="text-sm font-medium">
                  {clientsToPick[0].name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-light">dodaj</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </div>
              </button>
            ) : (
              <></>
            )}

            {openClient && (
              <div
                className="absolute w-full mt-1 border bg-white shadow-lg z-50"
                ref={clientDropdownRef}
              >
                {/* Search input for clients */}
                <div className="p-3 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Szukaj klientów..."
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>

                {/* Clients list */}
                <div className="max-h-60 overflow-auto">
                  {filteredClients.length > 0 ? (
                    filteredClients.map((client) => (
                      <button
                        key={client.id}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                        onClick={() => handleClientChange(client.id)}
                      >
                        {client.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Nie znaleziono klientów
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-1 overflow-x-auto pb-1 min-w-0 max-w-full">{showSelectedClients}</div>

          <button
            type="submit"
            className="gap-2 bg-black text-white px-4 py-2 w-24 mt-10 ml-auto"
            onClick={() => handleSumbit()}
          >
            Zapisz
          </button>
        </div>
      </div>
    </div>
  );
};

EditCourse.propTypes = {
  categories: PropTypes.array,
  clients: PropTypes.array,
  course: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    logo_url: PropTypes.string,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    clients: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
  }),
};

export default EditCourse;
